function renderPageContentActionButtonsTemplate(): string {
  return `
            <Button
              variant="outline"
              onClick={() => exportCsv.mutate()}
              disabled={exportCsv.isPending}
              aria-busy={exportCsv.isPending}
            >
              {exportCsv.isPending ? <Spinner className="size-3.5" /> : <Download className="size-3.5" />}
              {exportCsv.isPending ? 'Exporting CSV…' : 'Export CSV'}
            </Button>`
}

function renderPageContentHandleBulkDeleteTemplate({
  pascal,
  camel
}: {
  pascal: string
  camel: string
}): string {
  return `
  const handleBulkDelete = () => {
    startTransition(async () => {
      try {
        const result = await deleteBulk${pascal}Submissions(selectedIds)

        if (result.success) {
          toast.success(
            selectedIds.length === 1
              ? \`\${selectedIds.length} submission deleted\`
              : \`\${selectedIds.length} submissions deleted\`
          )
          queryClient.refetchQueries({ queryKey: ['${camel}-submissions'] })
          void setSelectedIds([])
        } else {
          toast.error(result.error || 'Failed to delete the selected submissions')
        }
      } catch (error) {
        toast.error('Failed to delete the selected submissions. Try again.')
        console.error(error)
      }
    })
  }
`
}

function renderPageContentLayoutTemplate(): string {
  return `
          actions={
            <div className="flex items-center gap-1">
              {selectedIds.length > 0 && (
                <DeleteDialog
                  count={selectedIds.length}
                  entityName="submission"
                  onConfirm={handleBulkDelete}
                  isPending={isPending}
                />
              )}
            </div>
          }`
}

function renderPageContentClientModuleTemplate({
  queryClientImport,
  exportIconImport,
  toastImport,
  deleteActionImport,
  deleteDialogImport,
  exportSpinnerImport,
  devModeIntegrateButtonExportName,
  devModeIntegrateButtonImportPath,
  exportHookImport,
  pascal,
  kebab,
  queryClient,
  camel,
  exportHook,
  deleteTransition,
  bulkDeleteHandler,
  label,
  exportButton,
  adminRoutePath,
  toLowerCase,
  bulkDeleteActions
}: {
  queryClientImport: string
  exportIconImport: string
  toastImport: string
  deleteActionImport: string
  deleteDialogImport: string
  exportSpinnerImport: string
  devModeIntegrateButtonExportName: string
  devModeIntegrateButtonImportPath: string
  exportHookImport: string
  pascal: string
  kebab: string
  queryClient: string
  camel: string
  exportHook: string
  deleteTransition: string
  bulkDeleteHandler: string
  label: string
  exportButton: string
  adminRoutePath: string
  toLowerCase: string
  bulkDeleteActions: string
}): string {
  return `'use client'

import type { Setting } from '@admin/actions/settings'
import { useIsFetching${queryClientImport} } from '@tanstack/react-query'
import { ${exportIconImport}Settings } from 'lucide-react'
import Link from 'next/link'
import { parseAsArrayOf, parseAsInteger, parseAsString, useQueryState, useQueryStates } from 'nuqs'
import * as React from 'react'
${toastImport}
${deleteActionImport}
${deleteDialogImport}
import { EntityFiltersBar } from '@admin/components/shared/entity-filters-bar'
import { PageHeader } from '@admin/components/shared/page-header'
import { Button } from '@admin/components/ui/button'${exportSpinnerImport}
import { ${devModeIntegrateButtonExportName} } from '${devModeIntegrateButtonImportPath}'${exportHookImport}
import { columns } from './columns'
import { ${pascal}SubmissionDrawer } from './${kebab}-submission-drawer'
import { ${pascal}SubmissionsTable } from './${kebab}-submissions-table'

interface ${pascal}SubmissionsPageContentProps {
  initialSettings?: Setting | null
}

export function ${pascal}SubmissionsPageContent({
  initialSettings
}: ${pascal}SubmissionsPageContentProps) {
${queryClient}  const isSearchFetching = useIsFetching({ queryKey: ['${camel}-submissions'] }) > 0${exportHook}
  const [search, setSearch] = useQueryState('q', parseAsString.withDefault(''))
  const [, setPageIndex] = useQueryState('page', parseAsInteger.withDefault(0))
  const [selectedSubmissionId, setSelectedSubmissionId] = useQueryState(
    'submission',
    parseAsString.withDefault('')
  )
  const filterParsers = {
    submittedAtFrom: parseAsString.withDefault(''),
    submittedAtTo: parseAsString.withDefault(''),
  } as const
  const [filterValues, setFilters] = useQueryStates(filterParsers)
  const submittedAtFrom = filterValues.submittedAtFrom
  const submittedAtTo = filterValues.submittedAtTo
  const [selectedIds, setSelectedIds] = useQueryState(
    'selected',
    parseAsArrayOf(parseAsString).withDefault([])
  )
${deleteTransition}${bulkDeleteHandler}

  const activeFilterCount = [search, (submittedAtFrom || submittedAtTo)].filter(Boolean).length

  const handleViewSubmission = React.useCallback((id: string) => {
    void setSelectedSubmissionId(id)
  }, [setSelectedSubmissionId])

  const handleSubmissionDrawerOpenChange = React.useCallback((open: boolean) => {
    if (!open) {
      void setSelectedSubmissionId(null)
    }
  }, [setSelectedSubmissionId])

  const handleSearch = React.useCallback((value: string | null) => {
    React.startTransition(() => {
      void setPageIndex(0)
      void setSearch(value)
    })
  }, [setSearch, setPageIndex])

  const handleClearAll = React.useCallback(() => {
    React.startTransition(() => {
      void setPageIndex(0)
      void setSearch(null)
      void setFilters({
        submittedAtFrom: null,
        submittedAtTo: null
      })
    })
  }, [setSearch, setFilters, setPageIndex])

  const filters = [
    {
      key: 'submittedAt',
      label: 'Submitted',
      type: 'date-range' as const,
      from: submittedAtFrom,
      to: submittedAtTo
    }
  ]

  return (
    <React.Fragment>
      <PageHeader
        title="${label}"
        actions={
          <div className="flex items-center gap-1">
            <${devModeIntegrateButtonExportName} initialSettings={initialSettings} />${exportButton}
            <Button variant="outline" render={<Link href="${adminRoutePath}/settings/forms" />}>
                <Settings className="size-3.5" />
                Settings
            </Button>
          </div>
        }
      />
      <EntityFiltersBar
          filters={filters}
          activeFilterCount={activeFilterCount}
          onFilterChange={(updates) => {
            React.startTransition(() => {
              void setPageIndex(0)
              void setFilters(updates)
            })
          }}
          onClearAll={handleClearAll}
          search={search}
          searchPlaceholder="Search ${toLowerCase} submissions..."
          isPending={isSearchFetching}
          onSearch={handleSearch}
${bulkDeleteActions}
        />
      <main className="space-y-4 px-4 pb-4 flex-1">
        <${pascal}SubmissionsTable
          columns={columns}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          onViewSubmission={handleViewSubmission}
          search={search}
          submittedAtFrom={submittedAtFrom}
          submittedAtTo={submittedAtTo}
        />
      </main>
      <${pascal}SubmissionDrawer
        open={Boolean(selectedSubmissionId)}
        submissionId={selectedSubmissionId || null}
        onOpenChange={handleSubmissionDrawerOpenChange}
      />
    </React.Fragment>
  )
}
`
}

export function renderAdminPageContentRenderer(
  request:
    | { kind: 'renderPageContentActionButtonsTemplate' }
    | {
        kind: 'renderPageContentHandleBulkDeleteTemplate'
        props: {
          pascal: string
          camel: string
        }
      }
    | { kind: 'renderPageContentLayoutTemplate' }
    | {
        kind: 'renderPageContentClientModuleTemplate'
        props: {
          queryClientImport: string
          exportIconImport: string
          toastImport: string
          deleteActionImport: string
          deleteDialogImport: string
          exportSpinnerImport: string
          devModeIntegrateButtonExportName: string
          devModeIntegrateButtonImportPath: string
          exportHookImport: string
          pascal: string
          kebab: string
          queryClient: string
          camel: string
          exportHook: string
          deleteTransition: string
          bulkDeleteHandler: string
          label: string
          exportButton: string
          adminRoutePath: string
          toLowerCase: string
          bulkDeleteActions: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderPageContentActionButtonsTemplate':
      return renderPageContentActionButtonsTemplate()
    case 'renderPageContentHandleBulkDeleteTemplate':
      return renderPageContentHandleBulkDeleteTemplate(request.props)
    case 'renderPageContentLayoutTemplate':
      return renderPageContentLayoutTemplate()
    case 'renderPageContentClientModuleTemplate':
      return renderPageContentClientModuleTemplate(request.props)
  }

  throw new Error('Unsupported renderer request')
}
